import { createSelector, createSlice } from "@reduxjs/toolkit";
import { ensureArray, groupBy } from "../common/utils/arrayUtilities";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, dataHookWithCredentialsFactory, DefaultFailureAction, DefaultSuccessAction, initMapFactory, queryMapFactory } from "../common/reducers/utilities";
import { aggregatingFetch } from "../common/hooks/networkHook";
import { dateInRangeInclusive, maxDate, minDate } from "../common/utils/dateUtils";
import { markFetched, registerEntityBasedFetch } from "../common/hooks/initHook";
import useCredentials from "../common/hooks/credentialsHook";

const key = qr => qr.id;

export const qualRequestSlice = createSlice({
    name: 'qualRequests',
    initialState: {
        types: [],
        data: {}
    },
    reducers: {
        set: (state, action) => {
            ensureArray(action.payload.ids).forEach(id => delete state.data[id]);
            ensureArray(action.payload.data).forEach(qr => state.data[key(qr)] = qr);
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(qr => state.data[key(qr)] = qr);
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(qr => delete state.data[key(qr)]);
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => qualRequestSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'qualificationRequest_one', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'qualificationRequest_one');

const fetchQualRequestsByID = aggregatingFetch(
    id => network => network.post('/api/v2/fetchQualRequests', [id], data => qualRequestSlice.actions.set({ ids: [id], data }), visualizeFailure('get')),
    ids => network => network.post('api/v2/fetchQualRequests', ids, data => qualRequestSlice.actions.addOrUpdate({ ids, data }), visualizeFailure('get'))
)
const fetchQualRequestsInRange = aggregatingFetch(
    (start, end) => network => network.get(`/api/v2/qualRequests/${encodeURIComponent(start)}/${encodeURIComponent(end)}`, qualRequestSlice.actions.addOrUpdate, visualizeFailure('get')),
    keys => network => network.get(`/api/v2/qualRequests/${encodeURIComponent(minDate(keys, 0))}/${encodeURIComponent(maxDate(keys, 1))}`, qualRequestSlice.actions.addOrUpdate, visualizeFailure('get'))
);

export const confirmQualRequest = (network, id) => network.put(`/api/v1/qualRequest/approve/${window.encodeURIComponent(id)}`, {}, qualRequestSlice.actions.addOrUpdate, visualizeFailure('update'));
export const rejectQualRequest = (network, id) => network.put(`/api/v1/qualRequest/reject/${window.encodeURIComponent(id)}`, {}, qualRequestSlice.actions.addOrUpdate, visualizeFailure('update'));
export const updateQualRequest  = (network, qualRequest) => network.put('/api/v1/qualRequest', qualRequest, qualRequestSlice.actions.addOrUpdate, visualizeFailure('update'));
export const deleteQualRequest = (network, qualRequest) => network.delete('/api/v1/qualRequest/' + encodeURIComponent(qualRequest.id), qualRequestSlice.actions.remove, visualizeFailure('remove'));

export const createQualRequest = (network, qualRequest) => {
    let data = new FormData();
    
    Object.entries(qualRequest).forEach(([ field, value ]) => {
        if (!['approved', 'approval_user', 'approval_date'].includes(field)) {
            data.append(field, value)
        }
    });

    if (qualRequest.file?.type) {
        data.append('mime', qualRequest.file.type);
    }

    if (qualRequest.approved && qualRequest.approval_user && qualRequest.approval_date) {
        data.append('approved', qualRequest.approved);
        data.append('approval_user', qualRequest.approval_user);
        data.append('approval_date', qualRequest.approval_date);
    }
    
    network.postData('/api/v1/qualRequest', data);
}

export const deleteqQalRequest = (network, qualRequest) =>  network.delete(`/api/v1/qualRequest/${window.encodeURIComponent(qualRequest.id)}`, qualRequestSlice.actions.remove, visualizeFailure('remove'));
// #endregion

// #region selectors
const selectAllQualRequests = state => state.qualRequests.data;
const selectAllQualRequestsByMember = createSelector([selectAllQualRequests], qualRequests => groupBy(Object.values(qualRequests), 'memberid'));
const selectMgrQualRequestsInRange = (start, end, memberid) => createSelector([selectAllQualRequests], qualRequests => Object.values(qualRequests).filter(qr => dateInRangeInclusive(qr.created, { start, end })) ?? []);
const selectMgrPendingQualRequestsInRange = (start, end, memberid) => createSelector([selectAllQualRequests], qualRequests => Object.values(qualRequests).filter(qr => qr.approved === null && dateInRangeInclusive(qr.created, { start, end })) ?? []);
const selectStaffQualRequestsInRange = (start, end, memberid) => createSelector([selectAllQualRequestsByMember], qualRequests => qualRequests[memberid]?.filter(qr => dateInRangeInclusive(qr.created, { start, end })) ?? []);

// #endregion

const listen = {
    memberQualRequestCreated: (qualRequest, { dispatch, websocket }) => {
        markFetched(websocket, 'qualRequest', key(qualRequest))
        dispatch(qualRequestSlice.actions.addOrUpdate(qualRequest))
    },
    memberQualRequestUpdated: (qualRequest, { dispatch }) => dispatch(qualRequestSlice.actions.addOrUpdate(qualRequest)),
    memberQualRequestRemoved: (qualRequest, { dispatch }) => dispatch(qualRequestSlice.actions.remove(qualRequest))
}

const qualRequestsInitByID = initMapFactory(fetchQualRequestsByID, listen);
registerEntityBasedFetch('qualRequest', qualRequestsInitByID);

const cleanup = { root: qualRequestSlice.name, all: state => Object.values(selectAllQualRequests(state)), removeAll: qualRequestSlice.actions.remove, key };

export const [useMgrQualRequestsInRange] = dataHookFactory(selectMgrQualRequestsInRange, queryMapFactory(fetchQualRequestsInRange, 'id', qualRequestsInitByID, listen, { cleanup }));
export const [useMgrPendingQualRequestsInRange] = dataHookFactory(selectMgrPendingQualRequestsInRange, queryMapFactory(fetchQualRequestsInRange, 'id', qualRequestsInitByID, listen, { cleanup }));
export const [useStaffQualRequestsInRange] = dataHookWithCredentialsFactory(selectStaffQualRequestsInRange, queryMapFactory(fetchQualRequestsInRange, 'id', qualRequestsInitByID, listen), {cleanup, condition: credentials => credentials?.hasRole('member') });

export const canRouteQualificationRequests = ({credentials}) => credentials.hasRole(['administrator', 'training manager', 'auditor', 'training auditor'])

export const useSelfQualRequestsInRange = (start, end) => { 
    const credentials = useCredentials();
    return useStaffQualRequestsInRange(start, end, credentials.memberID);
}

const reducer = { name: qualRequestSlice.name, reducer: qualRequestSlice.reducer }
export default reducer;